import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";
import {
  ArrowLeft, Upload, Wand2, RefreshCw, Check, X,
  FileArchive, ExternalLink, TriangleAlert, Settings,
} from "lucide-react";


export default function MaterialsThumbnailsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "overview";  // overview | needs_review

  const [stats, setStats] = useState(null);
  const [libraries, setLibraries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);   // supplier_registry rows
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);
  // Refs keyed by supplier name — allocated dynamically once the
  // registry loads. Uses a ref-of-map so we don't blow away references
  // across re-renders.
  const uploadRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const [s, l, r] = await Promise.all([
        api.get("/materials/thumbnails/status"),
        api.get("/supplier-libraries"),
        api.get("/supplier-registry"),
      ]);
      setStats(s.data); setLibraries(l.data); setSuppliers(r.data);
    } catch (e) { toast.error(formatErr(e)); }
    if (status === "needs_review") {
      try {
        const r = await api.get("/materials/thumbnails/candidates?status=needs_review");
        setCandidates(r.data);
      } catch (e) { toast.error(formatErr(e)); }
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const getUploadRef = (sup) => {
    if (!uploadRefs.current[sup]) uploadRefs.current[sup] = { current: null };
    return uploadRefs.current[sup];
  };

  const uploadLibrary = async (supplier, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) { toast.error("Please choose a .zip file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("supplier_name", supplier);
      const r = await api.post("/supplier-libraries", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${supplier}: indexed ${r.data.entry_count} assets`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const removeLibrary = async (id) => {
    if (!window.confirm("Remove this supplier library from Subtend Studio?")) return;
    try { await api.delete(`/supplier-libraries/${id}`); await load(); toast.success("Removed"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const runDiscovery = async () => {
    setBusy(true);
    try {
      const r = await api.post("/materials/thumbnails/discover");
      toast.success(`Auto-populated ${r.data.auto_applied} · ${r.data.needs_review} need review`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const acceptCandidate = async (cid, filename) => {
    try {
      await api.post(`/materials/thumbnails/candidates/${cid}/accept`, { filename });
      toast.success("Applied");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const rejectCandidate = async (cid) => {
    try {
      await api.post(`/materials/thumbnails/candidates/${cid}/reject`);
      toast.success("Rejected");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-6xl mx-auto" data-testid="thumbnails-page">
      <Link to="/materials" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Materials
      </Link>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3">Supplier thumbnails</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        Upload each registered supplier's official asset library ZIP once. Subtend Studio indexes it and
        auto-populates missing material thumbnails on exact, unambiguous matches. Full-sheet
        and high-resolution swatches are retained as separate assets.
      </p>
      <div className="mt-3">
        <Link to="/settings/supplier-libraries"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground"
              data-testid="link-supplier-registry">
          <Settings className="w-3 h-3" strokeWidth={1.5}/> Manage suppliers
        </Link>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-6 border-b border-border">
        {[
          {k: "overview",      label: "Overview"},
          {k: "needs_review",  label: `Needs review${stats ? ` (${stats.needs_review})` : ""}`},
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setParams({ status: t.k })}
            className={"pb-3 text-sm uppercase tracking-widest border-b-2 " +
              (status === t.k ? "border-foreground" : "border-transparent text-muted-foreground")}>
            {t.label}
          </button>
        ))}
      </div>

      {status === "overview" && (
        <>
          {/* Summary */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
            <Stat label="Materials" value={stats?.materials ?? "–"} testid="stat-materials"/>
            <Stat label="With thumbnail" value={stats?.with_thumbnail ?? "–"} testid="stat-with"/>
            <Stat label="Missing" value={stats?.missing ?? "–"} testid="stat-missing"/>
            <Stat label="Auto-populated" value={stats?.auto_populated ?? "–"} testid="stat-auto"/>
            <Stat label="Needs review" value={stats?.needs_review ?? "–"} testid="stat-review"/>
          </div>

          {/* Supplier libraries — data-driven from supplier_registry */}
          <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="libraries-block">
            {suppliers.length === 0 && (
              <div className="luxe-card p-6 md:col-span-2 text-sm text-muted-foreground"
                   data-testid="no-suppliers-hint">
                No suppliers registered yet. Add one from
                <Link to="/settings/supplier-libraries" className="underline mx-1">Settings → Supplier Asset Libraries</Link>
                to enable digital asset ingestion.
              </div>
            )}
            {suppliers.map((sup) => {
              const sName = sup.supplier_name;
              const active = libraries.filter((l) => l.supplier_name === sName);
              const providerReady = sup.provider_available && sup.enabled;
              return (
                <div key={sup.id} className="luxe-card p-6"
                     data-testid={`library-card-${sName.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="overline text-[10px]">Supplier</div>
                      <div className="font-serif text-xl mt-1">{sName}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {sup.provider_type}
                        {" · "}
                        <span className={sup.auto_populate_enabled ? "text-foreground" : ""}>
                          {sup.auto_populate_enabled
                            ? `Auto-populate ≥${sup.auto_populate_threshold}%`
                            : "Review-only"}
                        </span>
                      </div>
                    </div>
                    <FileArchive className="w-6 h-6 text-muted-foreground" strokeWidth={1.5}/>
                  </div>
                  {!providerReady && (
                    <div className="mt-3 text-[11px] uppercase tracking-widest text-amber-600"
                         data-testid={`hint-not-ready-${sName.toLowerCase()}`}>
                      {sup.provider_available ? "Disabled" : "Provider Setup Required"}
                    </div>
                  )}
                  {active.length === 0 && (
                    <div className="mt-4 text-xs text-muted-foreground">
                      No library uploaded yet. Download the official {sName} asset library ZIP from the supplier's approved portal, then upload it here.
                    </div>
                  )}
                  {active.length > 0 && (
                    <ul className="mt-4 space-y-2 text-xs">
                      {active.map((l) => (
                        <li key={l.id} className="flex items-center justify-between border-b border-border py-1"
                            data-testid={`lib-row-${l.id}`}>
                          <span className="truncate">
                            {l.filename} <span className="text-muted-foreground">· {l.entry_count} assets · {new Date(l.uploaded_at).toLocaleDateString("en-AU")}</span>
                          </span>
                          <button data-testid={`btn-remove-lib-${l.id}`}
                                  onClick={() => removeLibrary(l.id)}
                                  className="text-[10px] text-muted-foreground hover:text-foreground">
                            <X className="w-3 h-3" strokeWidth={1.5}/>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <input
                    ref={(el) => { getUploadRef(sName).current = el; }}
                    type="file" accept=".zip" className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadLibrary(sName, e.target.files[0])}
                    data-testid={`input-lib-${sName.toLowerCase()}`}/>
                  <button type="button" disabled={busy || !providerReady}
                          onClick={() => getUploadRef(sName).current?.click()}
                          className="btn-outline mt-4 w-full justify-center text-xs disabled:opacity-40"
                          data-testid={`btn-upload-lib-${sName.toLowerCase()}`}>
                    <Upload className="w-3 h-3" strokeWidth={1.5}/>
                    {active.length ? `Upload new ${sName} library` : `Upload ${sName} library ZIP`}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Action */}
          <div className="mt-10 flex items-center justify-between border border-border p-6">
            <div>
              <div className="overline text-[10px]">Bulk</div>
              <div className="font-serif text-lg mt-1">Find missing thumbnails</div>
              <div className="text-xs text-muted-foreground mt-1 max-w-md">
                Runs the deterministic matcher across every missing supplier material against your uploaded libraries. Exact matches auto-populate for suppliers where auto-populate is on; anything else goes to Needs review.
              </div>
            </div>
            <button data-testid="btn-run-discovery" disabled={busy || libraries.length === 0}
                    onClick={runDiscovery} className="btn-primary">
              <Wand2 className="w-4 h-4" strokeWidth={1.5}/>
              {busy ? "Running…" : "Find missing thumbnails"}
            </button>
          </div>

          <div className="text-[10px] text-muted-foreground mt-6 flex items-center gap-2">
            <TriangleAlert className="w-3 h-3" strokeWidth={1.5}/>
            Manual thumbnails are never overwritten. Pricing, project estimates and issued proposals are never changed by this process.
          </div>
        </>
      )}

      {status === "needs_review" && (
        <div className="mt-8 space-y-4" data-testid="needs-review-list">
          {candidates.length === 0 && (
            <div className="luxe-card p-10 text-center text-sm text-muted-foreground">
              Nothing needs review right now.
            </div>
          )}
          {candidates.map((c) => (
            <div key={c.id} className="luxe-card p-6" data-testid={`review-row-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline text-[10px]">{c.supplier_name}</div>
                  <div className="font-serif text-lg mt-1">Material {c.product_id}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Best guess: <strong>{c.matched_filename}</strong> · {c.confidence}% ({c.match_reasons.join(", ") || "n/a"})
                  </div>
                </div>
                <button data-testid={`btn-reject-${c.id}`} onClick={() => rejectCandidate(c.id)}
                        className="btn-outline text-xs">
                  <X className="w-3 h-3" strokeWidth={1.5}/> Reject
                </button>
              </div>
              <ul className="mt-4 space-y-2 text-xs">
                {(c.candidates || []).map((cand) => (
                  <li key={cand.filename}
                      className="flex items-center justify-between border-b border-border py-1"
                      data-testid={`cand-${c.id}-${cand.filename}`}>
                    <span>
                      <span className="mr-2 text-[color:hsl(var(--accent))]">{cand.confidence}%</span>
                      {cand.filename}
                      <span className="ml-2 text-muted-foreground">
                        {[cand.colour, cand.finish, cand.range].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <button
                      data-testid={`btn-accept-${c.id}-${cand.filename}`}
                      onClick={() => acceptCandidate(c.id, cand.filename)}
                      className="text-[10px] px-2 py-1 border border-input hover:border-foreground">
                      <Check className="w-3 h-3 inline mr-1" strokeWidth={1.5}/> Use this
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function Stat({ label, value, testid }) {
  return (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="overline text-[9px]">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}
